import { joinClasses } from '../../primitives/fieldClassSlots';
import { layoutInlineStyle } from '../layoutInlineStyle';
import type { LayoutNodeChildProps } from '../nodeRenderProps';
import type { FlexNode } from '../types';
import { RenderNodes } from '../renderNodes';

function flexAlignItemsClass(
  v: FlexNode['alignItems'] | undefined
): string | undefined {
  switch (v) {
    case 'start':
      return 'items-start';
    case 'center':
      return 'items-center';
    case 'end':
      return 'items-end';
    case 'stretch':
      return 'items-stretch';
    case 'baseline':
      return 'items-baseline';
    default:
      return undefined;
  }
}

// TODO: Remove after layouts migrate from deprecated `alignItems` to `className`.
function responsiveRowAlignItemsClass(
  v: FlexNode['alignItems'] | undefined
): string | undefined {
  switch (v) {
    case 'start':
      return 'md:items-start';
    case 'center':
      return 'md:items-center';
    case 'end':
      return 'md:items-end';
    case 'stretch':
      return 'md:items-stretch';
    case 'baseline':
      return 'md:items-baseline';
    default:
      return undefined;
  }
}

function flexJustifyContentClass(
  v: FlexNode['justifyContent'] | undefined
): string | undefined {
  switch (v) {
    case 'start':
      return 'justify-start';
    case 'center':
      return 'justify-center';
    case 'end':
      return 'justify-end';
    case 'between':
      return 'justify-between';
    case 'around':
      return 'justify-around';
    case 'evenly':
      return 'justify-evenly';
    default:
      return undefined;
  }
}

export function Flex({
  node,
  answers,
  repeatBindings,
}: LayoutNodeChildProps<FlexNode>) {
  const FLEX_VARIANT_COL_GAP: Record<'compact' | 'default' | 'loose', number> =
    {
      compact: 3,
      default: 4,
      loose: 6,
    };
  const FLEX_VARIANT_ROW_COLUMN_GAP: Record<
    'compact' | 'default' | 'loose',
    string
  > = {
    compact: 'gap-x-6',
    default: 'gap-x-8',
    loose: 'gap-x-10',
  };
  const FLEX_VARIANT_ROW_ROW_GAP: Record<
    'compact' | 'default' | 'loose',
    number
  > = {
    compact: 4,
    default: 6,
    loose: 8,
  };
  const variant = node.variant ?? 'default';
  const row = node.direction === 'row';
  const gapClass = row
    ? node.gap != null
      ? `gap-${node.gap}`
      : joinClasses(
          FLEX_VARIANT_ROW_COLUMN_GAP[variant],
          `gap-y-${FLEX_VARIANT_ROW_ROW_GAP[variant]}`
        )
    : `gap-${node.gap ?? FLEX_VARIANT_COL_GAP[variant]}`;
  return (
    <div
      className={[
        'flex',
        row ? 'flex-col items-stretch md:flex-row' : 'flex-col',
        node.wrap ? 'md:flex-wrap' : undefined,
        gapClass,
        '[&>*]:min-w-0 max-md:[&>*]:!w-full',
        row
          ? responsiveRowAlignItemsClass(node.alignItems)
          : flexAlignItemsClass(node.alignItems),
        flexJustifyContentClass(node.justifyContent),
        node.className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={layoutInlineStyle(node.style)}
    >
      <RenderNodes
        nodes={node.children}
        answers={answers}
        repeatBindings={repeatBindings}
      />
    </div>
  );
}
